import _ from 'lodash';

import { IpcEvents } from '@/ipc-events';

import { IS_BROWSER, IS_DEV } from '@/common/constant/platform';
import ipc from '../renderer/scripts/utils/ipc';

interface Logger {
  (...args: any[]): void;
  trace(...args: any[]): void;
  debug(...args: any[]): void;
  info(...args: any[]): void;
  warn(...args: any[]): void;
  error(...args: any[]): void;
  fatal(...args: any[]): void;
  mark(...args: any[]): void;
}

enum LevelTag {
  trace = 'trace',
  debug = 'debug',
  info = 'info',
  warn = 'warn',
  error = 'error',
  fatal = 'fatal',
  mark = 'mark',
}
const levels: LevelTag[] = Object.values(LevelTag);

const Log4jsToConsoleLogMap: Record<string, string> = {
  trace: 'log',
  debug: 'log',
  info: 'info',
  warn: 'warn',
  error: 'error',
  fatal: 'fatal',
  mark: 'mark',
};

enum LogCategorieTag {
  main = 'main',
  smh = 'smh',
  ui = 'ui',
  worker = 'worker',
}
const logCategories = Object.values(LogCategorieTag); // 这里的类型需和日志配置的地方保持一致

/**
 * 提供一种类别，渲染进行写日志的时候可用，log.smh(), log.opt.error()等，不写级别时默认debug，同时默认调用了console.log
 */
const log = {} as any;

function logTraceLine() {
  let line = '';
  const trace = new Error().stack?.split('\n');
  if (trace) {
    const index = trace.findIndex(i => i.includes('logger'));
    const lineMatch = trace[index + 1].match(/.*\((.*)\)/);
    line = lineMatch ? lineMatch[1] : '';
  }
  return line;
}

function sanitizeArgs(data: any): any {
  const copyData = _.cloneDeep(data);
  if (_.isString(copyData)) {
    return /userToken/g.test(copyData) ? '' : copyData;
  }
  if (_.isArray(copyData)) {
    return copyData.map(sanitizeArgs);
  }
  if (_.isObject(copyData)) {
    const obj = copyData as any;
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (key === 'userToken') {
        delete obj[key];
      } else {
        obj[key] = sanitizeArgs(value);
      }
    });
  }
  return copyData;
}

logCategories.forEach(key => {
  log[key] = ((...args) => {
    function logger(tag: string, level: string) {
      return function realLogger(...args: any[]) {
        const _args = args.filter(v => v); // 过滤掉空打印
        if (_args.length === 0) return;
        if (IS_BROWSER && !IS_DEV) return;

        try {
          const grade = Log4jsToConsoleLogMap[level] || 'log';
          const line = logTraceLine();
          // @ts-ignore
          console[grade](..._args, line && `\n[line: ${line}]`);

          if (IS_DEV) return; // 渲染进程日志在本地开发模式下被关闭，需要调试的话可在此打开

          const safeArgs = _args.map(arg => {
            const sanitizeArg = sanitizeArgs(arg);
            if (typeof sanitizeArg === 'string') {
              return sanitizeArg;
            }

            try {
              const _arg = JSON.stringify(sanitizeArg);
              return _arg;
            } catch (error) {
              return sanitizeArg;
            }
          });

          if (safeArgs.length) {
            safeArgs.unshift(`[PID:${process.pid}]`);
          }
          ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
            event: 'diffLog',
            params: { type: tag, level, messages: safeArgs },
          });
        } catch (error) {
          console.error(error);
        }
      };
    }

    const log = logger(key, 'debug', ...args);

    levels.forEach((level: Level) => {
      (log as Logger)[level] = logger(key, level, ...args);
    });

    return log as Logger;
  })();
});
window.logger = log;
export default log as { smh: Logger; main: Logger; ui: Logger; worker: Logger };
